/**
 * What a player may legally do right now. Shared by the UI and the bots, so both
 * work from exactly the same answer. See scope.md §3.4.
 */

import {
  type Card, type GameState, type JokerStandsFor, type Meld, type PlayerId, type PlayerState,
  type RoomRules, isJoker,
} from '@garbage/shared';
import type { NaturalRank, Suit } from '@garbage/shared';
import { type Contract, contractFor } from './contracts.js';
import { extendMeld, jokerOptionsFor } from './melds.js';
import { findContract } from './search.js';
import { mayClaim } from './redact.js';
import { deadRoundOffered } from './take.js';

export type LegalAction =
  | 'DRAW_STOCK' | 'DRAW_DISCARD' | 'GO_DOWN' | 'LAY_OFF' | 'SWAP_JOKER' | 'DISCARD'
  | 'CLAIM' | 'READY' | 'DEAD_ROUND_VOTE';

export function canLayOff(state: GameState, player: PlayerState): boolean {
  if (state.rules.layOff === 'never') return false;
  if (!player.down) return false;
  if (state.rules.layOff === 'turnAfter' && player.justWentDown) return false;
  return true;
}

/** Melds this player is allowed to touch when laying off. */
export function layOffTargets(state: GameState, player: PlayerState): Meld[] {
  if (!canLayOff(state, player)) return [];
  return state.rules.layOffOnOthers
    ? state.melds
    : state.melds.filter((m) => m.ownerId === player.id);
}

export type LayOffOption = {
  meldId: string;
  cardId: string;
  /** Present when the card is a joker: what it would be standing in for. */
  joker?: { rank: NaturalRank; suit?: Suit };
};

/**
 * Every (meld, card) pair that would actually fit, jokers included. A joker carries
 * the declaration it would need, so the interface never has to ask the player what
 * their wild card is pretending to be.
 */
export function layOffOptions(state: GameState, player: PlayerState): LayOffOption[] {
  const out: LayOffOption[] = [];
  for (const meld of layOffTargets(state, player)) {
    for (const card of player.hand) {
      if (isJoker(card)) {
        for (const stands of jokerOptionsFor(meld, card, state.rules)) {
          out.push({
            meldId: meld.id,
            cardId: card.id,
            joker: { rank: stands.rank, ...(stands.suit ? { suit: stands.suit } : {}) },
          });
        }
        continue;
      }
      if (extendMeld(meld, card, state.rules)) out.push({ meldId: meld.id, cardId: card.id });
    }
  }
  return out;
}

/** A joker sitting in a meld, what it stands for, and the card itself. */
type TableJoker = { meld: Meld; stands: JokerStandsFor; card: Card };

/** Every joker on the table this player's rules let them take. */
function takeableJokers(melds: readonly Meld[], playerId: PlayerId, rules: RoomRules): TableJoker[] {
  if (rules.jokerSwap === 'off') return [];
  return melds
    .filter((meld) => !(rules.jokerSwap === 'othersOnly' && meld.ownerId === playerId))
    .flatMap((meld) => meld.jokers.flatMap((stands) => {
      const card = meld.cards.find((c) => c.id === stands.cardId);
      return card ? [{ meld, stands, card }] : [];
    }));
}

/** Cards in hand that may be given for this joker: the exact card in a run, the rank in a set. */
function givesFor(hand: readonly Card[], { meld, stands }: TableJoker): Card[] {
  return hand.filter((c) => !isJoker(c) && c.rank === stands.rank
    && (meld.kind !== 'run' || c.suit === stands.suit));
}

/**
 * Can this hand make the contract, taking any of these jokers along the way? Each is
 * paid for with a card from the hand, so which card goes matters; identical faces are
 * tried once. There are only ever a handful of jokers on the table.
 */
function reachesContract(
  hand: readonly Card[], jokers: readonly TableJoker[], contract: Contract, rules: RoomRules,
): boolean {
  if (findContract(hand, contract, rules) !== null) return true;
  return jokers.some((joker, i) => {
    const tried = new Set<string>();
    return givesFor(hand, joker).some((given) => {
      const face = `${given.rank}-${given.suit}`;
      if (tried.has(face)) return false;
      tried.add(face);
      const next = [...hand.filter((c) => c.id !== given.id), joker.card];
      return reachesContract(next, jokers.slice(i + 1), contract, rules);
    });
  });
}

/** What a swap decision needs to know, whether it comes from the state or a player's view. */
export type SwapTable = {
  playerId: PlayerId;
  down: boolean;
  hand: readonly Card[];
  melds: readonly Meld[];
  round: number;
  rules: RoomRules;
};

/**
 * May this player take that joker, giving `cardId` for it?
 *
 * Either they are already down, or — and this is the case that matters — taking the
 * joker is what *lets* them go down this turn. That may take more than one joker: a
 * player holding the real cards for two wilds, who can lay the contract once they have
 * both, may take the first and then the second. scope.md §3.6.
 */
export function swapAllowedAt(table: SwapTable, jokerCardId: string, cardId: string): boolean {
  if (table.down) return true;
  const jokers = takeableJokers(table.melds, table.playerId, table.rules);
  const taking = jokers.find((j) => j.card.id === jokerCardId);
  if (!taking) return false;
  const afterSwap = [...table.hand.filter((c) => c.id !== cardId), taking.card];
  const others = jokers.filter((j) => j !== taking);
  return reachesContract(afterSwap, others, contractFor(table.round, table.rules), table.rules);
}

export function swapIsAllowed(
  state: GameState, player: PlayerState, jokerCard: Card, cardId: string,
): boolean {
  return swapAllowedAt(swapTableFor(state, player), jokerCard.id, cardId);
}

function swapTableFor(state: GameState, player: PlayerState): SwapTable {
  return {
    playerId: player.id, down: player.down, hand: player.hand,
    melds: state.melds, round: state.round, rules: state.rules,
  };
}

/**
 * Every swap open to this player, from either the state or their view. Once down, a
 * joker already taken has to be placed before another; before going down, taking
 * several in a row is how a hand gets there.
 */
export function swapOptionsAt(
  table: SwapTable, pendingJokerIds: readonly string[],
): { meldId: string; jokerCardId: string; cardId: string }[] {
  if (table.down && pendingJokerIds.length > 0) return [];
  return takeableJokers(table.melds, table.playerId, table.rules).flatMap((joker) => givesFor(table.hand, joker)
    .filter((card) => swapAllowedAt(table, joker.card.id, card.id))
    .map((card) => ({ meldId: joker.meld.id, jokerCardId: joker.card.id, cardId: card.id })));
}

/** Jokers on the table this player could swap for, given what they hold. */
export function jokerSwapOptions(
  state: GameState, player: PlayerState,
): { meldId: string; jokerCardId: string; cardId: string }[] {
  return swapOptionsAt(swapTableFor(state, player), player.pendingJokerIds);
}

/**
 * Could this hand lay the round's contract? Answering properly means searching the
 * hand for a partition into the required melds, which is why the UI lets a player
 * build melds by hand rather than guessing for them. This is the cheap check: enough
 * cards, and enough of the right shapes to be worth offering the button.
 */
export function mightGoDown(state: GameState, player: PlayerState): boolean {
  if (player.down) return false;
  const contract = contractFor(state.round, state.rules);
  // In the final round the whole hand is laid, so the hand size is the requirement.
  if (contract.useWholeHand) {
    const minimum = contract.requirements.reduce((total, r) => total + r.length, 0);
    return player.hand.length >= minimum;
  }
  const needed = contract.requirements.reduce((total, r) => total + r.length, 0);
  return player.hand.length >= needed;
}

export function legalActions(state: GameState, playerId: PlayerId): LegalAction[] {
  const out: LegalAction[] = [];
  const player = state.players.find((p) => p.id === playerId);
  if (!player) return out;

  // During the briefing the only thing anybody can do is say they are ready.
  if (state.status === 'briefing') {
    if (!player.ready) out.push('READY');
    return out;
  }
  if (state.status !== 'playing') return out;

  // Anyone may vote on a round that is going nowhere, whoever's turn it is. §3.8.
  if (deadRoundOffered(state) && (state.deadRoundVotes ?? {})[playerId] === undefined) out.push('DEAD_ROUND_VOTE');

  if (state.claimWindow) {
    if (mayClaim(state, playerId)) out.push('CLAIM');
    return out;
  }

  if (state.turn.playerId !== playerId) return out;

  if (state.turn.phase === 'draw') {
    // Having asked for the discard during the window, the next player must take from it. §3.5.
    // An empty stock is rebuilt from a pile of two or more; with both piles empty, drawing
    // is what ends the round (§3.8), so it must still be on offer.
    if (!state.turn.mustTakeDiscard && (state.stock.length > 0 || state.discard.length !== 1)) {
      out.push('DRAW_STOCK');
    }
    if (state.discard.length > 0) out.push('DRAW_DISCARD');
    return out;
  }

  // phase 'play'
  if (mightGoDown(state, player)) out.push('GO_DOWN');
  if (layOffOptions(state, player).length > 0) out.push('LAY_OFF');
  if (jokerSwapOptions(state, player).length > 0) out.push('SWAP_JOKER');
  // A joker taken from a meld has to be placed before the turn can end. §3.6.
  if (player.pendingJokerIds.length === 0 && player.hand.length > 0) out.push('DISCARD');
  return out;
}

export type { Card };
